import React from 'react';
import {
	Card, Row, Col, Button, Modal, Form, DatePicker,
	message, Tag, Tooltip, Typography, Pagination, Divider,
} from 'antd';
import {
	HistoryOutlined, RedoOutlined, CheckCircleOutlined, WarningOutlined,
	CloseCircleOutlined, EyeOutlined, GlobalOutlined, DownOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, ajaxHandler, setConfig, extractResult } from '../../store/actions';
import Config from '../../Config';
import { Helmet } from 'react-helmet';
import ErrorAlertModal from '../../components/ErrorAlertModal';
import { groupActivityByNopolTanggal, ambilNamaPemilikGrup, ambilIpGrup } from '../../utils/activityLogGrouping';
import DetailAktivitasCollapse from '../../components/activitylog/DetailAktivitasCollapse';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const configClass = {
	title: 'Log Aktivitas Saya',
	apiUrl: { data: 'activitylog/me/' },
};

const config = new Config();

// Tambah entri baru di sini kalau event lain (tetapkan/bayar/dll) mulai
// dicatat - TIDAK perlu ubah backend, cuma label tampilan.
const EVENT_LABEL = {
	login: 'Login',
	cek_nopol_eri: 'Panggil Nopol-[ERI]',
	cek_nopol_lama: 'Panggil Nopol-[Lama]',
	daftar: 'Daftar',
	tetapkan: 'Penetapan',
	hapus_penetapan: 'Batal Penetapan',
};

const STATUS_CFG = {
	success: { label: 'Sukses', bg: '#f6ffed', border: '#b7eb8f', color: '#389e0d', icon: <CheckCircleOutlined /> },
	failed:  { label: 'Gagal',  bg: '#fff1f0', border: '#ffa39e', color: '#cf1322', icon: <WarningOutlined /> },
};

const StatusTag = ({ value }) => {
	const s = STATUS_CFG[value] || { label: String(value || '—'), bg: '#f5f5f5', border: '#d9d9d9', color: '#666', icon: null };
	return (
		<Tag icon={s.icon} style={{
			borderRadius: 20, fontWeight: 700, fontSize: 12,
			background: s.bg, borderColor: s.border, color: s.color,
			padding: '2px 10px', display: 'inline-flex', alignItems: 'center', gap: 4,
		}}>
			{s.label}
		</Tag>
	);
};

class LogAktivitasSaya extends React.Component {
	formRef = React.createRef();

	constructor(props) {
		super(props);
		this.state = {
			errMsg: '', errTitle: '',
			loadingData: false,
			pagination: { current: 1, pageSize: 10, total: 0 },
			dataSource: [],
			filterTanggal: null,
			isMobile: window.innerWidth <= 768,
			visibleModal: false,
			dataModal: null,
			// Kartu grup (nomor_polisi+tanggal) - default TERTUTUP semua,
			// toggle per kartu lewat key-nya. Lihat diskusi rencana 2026-10-17.
			expandedGroups: {},
		};
	}

	UNSAFE_componentWillMount() { this.props.initHandler(); }

	componentDidMount() {
		this.getData();
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
	}

	// PENTING: endpoint ini baca tanggal_mulai/tanggal_akhir via
	// django.utils.dateparse.parse_date (format ISO YYYY-MM-DD) - BUKAN
	// DD-MM-YYYY yang dipakai mayoritas form lain di project ini
	// (DATE_INPUT_FORMATS, cuma berlaku di serializer DRF biasa, endpoint
	// ini baca query param manual).
	_buildParams(page = 1) {
		const { pagination, filterTanggal } = this.state;
		const limit = pagination.pageSize;
		const offset = (page - 1) * limit;
		let q = `?limit=${limit}&offset=${offset}`;
		if (filterTanggal && filterTanggal[0]) q += `&tanggal_mulai=${filterTanggal[0].format('YYYY-MM-DD')}`;
		if (filterTanggal && filterTanggal[1]) q += `&tanggal_akhir=${filterTanggal[1].format('YYYY-MM-DD')}`;
		return q;
	}

	async getData(page = 1) {
		this.setState({ loadingData: true });
		const params = this._buildParams(page);
		this.props.ajaxViewHandler('get', configClass.apiUrl.data + params)
			.then((action) => {
				const { isSuccess, responseMsg } = extractResult(action);
				if (isSuccess) {
					this.setState(prev => ({
						dataSource: responseMsg.results || [],
						pagination: { ...prev.pagination, current: page, total: responseMsg.count || 0 },
					}));
				} else {
					this.setState({ errTitle: 'Gagal memuat data', errMsg: responseMsg });
					message.error({ content: 'Gagal memuat data', duration: 2, key: 'toast' });
				}
				this.setState({ loadingData: false });
			})
			.catch(() => {
				message.error({ content: 'Gagal request data, coba lagi', duration: 2, key: 'toast' });
				this.setState({ loadingData: false });
			});
	}

	handleFilter = () => this.getData(1);

	handleReset = () => {
		this.setState({ filterTanggal: null }, () => this.getData(1));
		if (this.formRef.current) this.formRef.current.resetFields();
	};

	showDetail = (row) => this.setState({ visibleModal: true, dataModal: row });

	toggleGroup = (key) => this.setState(prev => ({
		expandedGroups: { ...prev.expandedGroups, [key]: !prev.expandedGroups[key] },
	}));

	renderDetailModal() {
		const { visibleModal, dataModal, isMobile } = this.state;
		if (!dataModal) return null;
		return (
			<Modal
				visible={visibleModal}
				onCancel={() => this.setState({ visibleModal: false })}
				footer={null} title={null} closable={false}
				width={isMobile ? 'calc(100vw - 16px)' : 640}
				style={isMobile ? { top: 12, margin: '0 8px' } : { top: 40 }}
				bodyStyle={{ padding: 0 }}
				maskClosable destroyOnClose
			>
				<div style={{ background: 'linear-gradient(135deg, #0f2847 0%, #1a3a6b 100%)', borderRadius: isMobile ? 0 : '10px 10px 0 0', padding: isMobile ? '16px 18px' : '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						<HistoryOutlined style={{ color: '#fff', fontSize: 20 }} />
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{EVENT_LABEL[dataModal.event_type] || dataModal.event_type}</div>
							<div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>{moment(dataModal.timestamp).format('DD-MM-YYYY HH:mm:ss')}</div>
						</div>
					</div>
					<Button type="text" icon={<CloseCircleOutlined style={{ fontSize: 18 }} />} onClick={() => this.setState({ visibleModal: false })} style={{ color: 'rgba(255,255,255,0.8)' }} />
				</div>
				<div style={{ padding: '18px 24px', maxHeight: isMobile ? '65vh' : '70vh', overflowY: 'auto' }}>
					<div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
						<StatusTag value={dataModal.status} />
						{dataModal.ip_address && <Tag icon={<GlobalOutlined />} style={{ borderRadius: 20 }}>{dataModal.ip_address}</Tag>}
					</div>
					<DetailAktivitasCollapse detail={dataModal.detail} />
				</div>
				<Divider style={{ margin: 0 }} />
				<div style={{ padding: '12px 24px', display: 'flex', justifyContent: 'flex-end', background: '#fafafa', borderRadius: isMobile ? 0 : '0 0 10px 10px' }}>
					<Button onClick={() => this.setState({ visibleModal: false })} style={{ borderRadius: 8 }}>Tutup</Button>
				</div>
			</Modal>
		);
	}

	// Timeline vertikal di dalam 1 kartu grup (nomor_polisi + tanggal) -
	// urutan event kronologis naik (paling lama di atas, sesuai arahan
	// "urutannya ada di bawahnya sesuai waktunya").
	renderTimeline(rows) {
		return (
			<div style={{ position: 'relative', paddingLeft: 22 }}>
				{rows.length > 1 && (
					<div style={{ position: 'absolute', left: 4, top: 10, bottom: 10, width: 2, background: '#e2e8f5' }} />
				)}
				{rows.map((row, idx) => {
					const s = STATUS_CFG[row.status] || { color: '#999' };
					return (
						<div key={row.id} style={{ position: 'relative', marginBottom: idx === rows.length - 1 ? 0 : 16 }}>
							<div style={{
								position: 'absolute', left: -22, top: 4, width: 10, height: 10, borderRadius: '50%',
								background: s.color, border: '2px solid #fff', boxShadow: `0 0 0 2px ${s.color}33`,
							}} />
							<div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
								<div>
									<Text style={{ fontWeight: 700, fontSize: 13, color: '#0f2847' }}>{EVENT_LABEL[row.event_type] || row.event_type}</Text>
									<div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{moment(row.timestamp).format('DD-MM-YYYY HH:mm:ss')}</div>
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
									<StatusTag value={row.status} />
									<Tooltip title="Lihat Detail">
										<Button size="small" shape="circle" type="primary" ghost onClick={() => this.showDetail(row)}><EyeOutlined /></Button>
									</Tooltip>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	renderGroupCard(group) {
		const nama = ambilNamaPemilikGrup(group);
		const ip = ambilIpGrup(group);
		const terbuka = !!this.state.expandedGroups[group.key];
		const metaTagStyle = { borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fff', borderColor: '#e2e8f5', color: '#667085', margin: 0 };
		return (
			<Card key={group.key} style={{ borderRadius: 14, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>
				<div
					onClick={() => this.toggleGroup(group.key)}
					style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #0f284711, #1a3a6b11)', borderBottom: terbuka ? '1px solid #d0dff5' : 'none', padding: '12px 16px' }}
				>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
							<Tag style={{ borderRadius: 4, fontWeight: 700, fontSize: 13, fontFamily: 'monospace', background: '#0f2847', color: '#fff', borderColor: '#0f2847', letterSpacing: 1, margin: 0 }}>
								{group.nomor_polisi}
							</Tag>
							{nama && <Text style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{nama}</Text>}
							<Tag style={metaTagStyle}>{group.rows.length} aktivitas</Tag>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
							{ip && <Tag icon={<GlobalOutlined />} style={{ ...metaTagStyle, fontFamily: 'monospace' }}>{ip}</Tag>}
							<Text style={{ fontSize: 12, color: '#888' }}>{moment(group.tanggal).format('DD MMMM YYYY')}</Text>
							<DownOutlined style={{ fontSize: 11, color: '#888', transition: 'transform 0.2s', transform: terbuka ? 'rotate(180deg)' : 'rotate(0deg)' }} />
						</div>
					</div>
				</div>
				{terbuka && (
					<div style={{ padding: '16px 16px 14px' }}>
						{this.renderTimeline(group.rows)}
					</div>
				)}
			</Card>
		);
	}

	renderSingleCard(row) {
		return (
			<Card key={row.id} style={{ borderRadius: 14, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', overflow: 'hidden' }} bodyStyle={{ padding: '12px 14px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
					<div>
						<div style={{ fontWeight: 700, fontSize: 14, color: '#0f2847' }}>{EVENT_LABEL[row.event_type] || row.event_type}</div>
						<div style={{ marginTop: 5 }}><StatusTag value={row.status} /></div>
					</div>
					<Tooltip title="Lihat Detail">
						<Button size="small" shape="circle" type="primary" ghost onClick={() => this.showDetail(row)}><EyeOutlined /></Button>
					</Tooltip>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 7, borderTop: '1px dashed #eee' }}>
					<Text style={{ fontSize: 11, color: '#888' }}>{moment(row.timestamp).format('DD-MM-YYYY HH:mm:ss')}</Text>
					{row.ip_address && <Text style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>· {row.ip_address}</Text>}
				</div>
			</Card>
		);
	}

	renderList() {
		const items = groupActivityByNopolTanggal(this.state.dataSource);
		if (items.length === 0) {
			return <div style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>Tidak ada data</div>;
		}
		return items.map(item => item.type === 'group' ? this.renderGroupCard(item) : this.renderSingleCard(item.row));
	}

	render() {
		const { errMsg, errTitle, loadingData, pagination, isMobile, filterTanggal } = this.state;

		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
				</Helmet>

				<ErrorAlertModal visible={errMsg !== ''} title={errTitle} error={errMsg} onClose={() => this.setState({ errTitle: '', errMsg: '' })} />
				{this.renderDetailModal()}

				<Card className="actlog-wrap" style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>

					<div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0f2847, #1a3a6b)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
								<HistoryOutlined style={{ marginRight: 8 }} />{configClass.title}
							</div>
							<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
								Riwayat aktivitas akun saya · Total {pagination.total || 0} record{loadingData && ' · Memuat...'}
							</div>
						</div>
						<Tooltip title="Refresh">
							<Button icon={<RedoOutlined />} onClick={() => this.getData(pagination.current)} style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}>
								{!isMobile && 'Refresh'}
							</Button>
						</Tooltip>
					</div>

					<div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
						<Form ref={this.formRef} layout="vertical" onFinish={this.handleFilter}>
							<Row gutter={[12, 0]} align="bottom">
								<Col xs={24} sm={14} md={10} lg={8}>
									<Form.Item label="Rentang Tanggal (default 3 hari terakhir)" style={{ marginBottom: 0 }}>
										<RangePicker
											style={{ width: '100%', borderRadius: 8 }}
											format="DD-MM-YYYY"
											value={filterTanggal}
											onChange={(v) => this.setState({ filterTanggal: v })}
											placeholder={['Dari tanggal', 'Sampai tanggal']}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={10} md={8} lg={6}>
									<Form.Item style={{ marginBottom: 0 }}>
										<div style={{ display: 'flex', gap: 8, marginTop: isMobile ? 8 : 0 }}>
											<Button onClick={this.handleReset} style={{ borderRadius: 8, flex: isMobile ? 1 : 'unset' }}>Reset</Button>
											<Button type="primary" htmlType="submit" loading={loadingData}
												style={{ background: 'linear-gradient(135deg, #0f2847, #1a3a6b)', border: 'none', borderRadius: 8, fontWeight: 600, flex: isMobile ? 1 : 'unset' }}>
												Filter
											</Button>
										</div>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</div>

					<div style={{ padding: isMobile ? 12 : 20 }}>
						{loadingData
							? <div style={{ textAlign: 'center', padding: 48, color: '#aaa' }}>Memuat data...</div>
							: this.renderList()
						}
						{pagination.total > 0 && (
							<div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
								<Pagination
									current={pagination.current} pageSize={pagination.pageSize} total={pagination.total}
									onChange={(page) => this.getData(page)}
									showSizeChanger={!isMobile} simple={isMobile}
									pageSizeOptions={['10', '25', '50', '100']}
									onShowSizeChange={(page, pageSize) => {
										this.setState(prev => ({ pagination: { ...prev.pagination, pageSize } }), () => this.getData(page));
									}}
								/>
							</div>
						)}
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;
export default connect(mapStateToProps, { initHandler, setConfig, ajaxViewHandler, ajaxHandler })(LogAktivitasSaya);
